import { z } from 'zod/v4';

/**
 * Classification of the failure that ended a sync run, derived from the LAST
 * observed amfori HTTP status once the job-queue retry budget is exhausted:
 *
 * - `AUTH_FAILED`         — last status 401 (token rejected after re-mint).
 * - `CREDENTIALS_REVOKED` — an AUTH 403: amfori revoked the credentials.
 * - `DATA_SCOPE_REJECTED` — a DATA 403: the credentials are valid, but we asked
 *   for ids outside the member's supply chain. amfori answers both cases with
 *   403, so this is distinguished by the response body — see
 *   `isAmforiDataScopeForbidden`. It must NEVER be treated as a credential
 *   problem: doing so previously stamped `amfori_credentials_revoked_at` on a
 *   healthy integration and silently excluded the brand from every later sync.
 * - `RATE_LIMITED`        — last status 429.
 * - `UPSTREAM_5XX`        — last status 500–599 (incl. 503).
 * - `UNKNOWN`             — anything else (e.g. 400, 404) or no HTTP status.
 */
export const AmforiRequestFailureCodeSchema = z.enum([
  'AUTH_FAILED',
  'CREDENTIALS_REVOKED',
  'DATA_SCOPE_REJECTED',
  'RATE_LIMITED',
  'UPSTREAM_5XX',
  'UNKNOWN',
]);

export type AmforiRequestFailureCode = z.infer<
  typeof AmforiRequestFailureCodeSchema
>;

/**
 * Structured `last_error` persisted on a FAILED `amfori_sync_history` row (and
 * carried on the `AMFORI_SYNC_REPORT` notification). NULL/absent for COMPLETED
 * and PARTIAL runs.
 *
 * - `code`        — the classified failure category (drives notification copy).
 * - `message`     — short human-readable cause.
 * - `failedPhase` — the `job_name` running when the failure occurred.
 * - `httpStatus`  — the last observed amfori HTTP status, or null when none.
 */
export const AmforiSyncLastErrorSchema = z
  .object({
    code: AmforiRequestFailureCodeSchema,
    message: z.string(),
    failedPhase: z.string(),
    httpStatus: z.number().int().nullable(),
  })
  .strict();

export type AmforiSyncLastError = z.infer<typeof AmforiSyncLastErrorSchema>;
